// Founders / Beta pricing promotion configuration
// To disable the promotion, set FOUNDERS_PRICING_ENABLED to false
// or let the end date pass - the app will automatically revert to regular pricing
export const FOUNDERS_PRICING = {
  enabled: false,
  endDate: new Date('2026-07-01T23:59:59Z'),
  monthly: {
    price: 20,
    priceId: 'price_1T1qqtGHho15WvCDS9iZcyUj'
  },
  annual: {
    price: 216,
    priceId: 'price_1T1qqtGHho15WvCD2icr7MBe'
  },
  label: 'Founders Pricing',
  tagline: 'Lock in this price for the lifetime of your subscription'
};

export function isFoundersPricingActive(): boolean {
  return FOUNDERS_PRICING.enabled && new Date() < FOUNDERS_PRICING.endDate;
}

export function getActivePrice(interval: 'monthly' | 'annual'): { price: number; priceId: string } {
  if (isFoundersPricingActive()) {
    return FOUNDERS_PRICING[interval];
  }
  return {
    price: PRODUCTS[interval].price[interval as keyof typeof PRODUCTS[typeof interval]['price']] as number,
    priceId: PRODUCTS[interval].priceId
  };
}

export function getRegularPrices(): { monthly: number; annual: number } {
  return {
    monthly: PRODUCTS.monthly.price.monthly,
    annual: PRODUCTS.annual.price.annual
  };
}

export function getActivePriceDisplay(): { monthly: number; annual: number; regularMonthly: number; regularAnnual: number; isFounders: boolean; savingsPercent: number } {
  const regular = getRegularPrices();
  if (isFoundersPricingActive()) {
    const activeAnnual = FOUNDERS_PRICING.annual.price;
    const activeMonthly = FOUNDERS_PRICING.monthly.price;
    const savings = Math.round((1 - activeAnnual / (activeMonthly * 12)) * 100);
    return {
      monthly: activeMonthly,
      annual: activeAnnual,
      regularMonthly: regular.monthly,
      regularAnnual: regular.annual,
      isFounders: true,
      savingsPercent: savings
    };
  }
  const savings = Math.round((1 - regular.annual / (regular.monthly * 12)) * 100);
  return {
    monthly: regular.monthly,
    annual: regular.annual,
    regularMonthly: regular.monthly,
    regularAnnual: regular.annual,
    isFounders: false,
    savingsPercent: savings
  };
}

// Define the single Stripe product with monthly and annual pricing
export const PRODUCTS = {
  monthly: {
    id: 'prod_SUMojgy2r1hQuZ',
    name: 'SermonBuild Pro',
    description: 'Full access to all SermonBuild features - monthly billing.',
    price: { monthly: 30 },
    features: [
      'Unlimited sermons',
      'Unlimited sermon analyses',
      'Advanced formatting tools',
      'Series management',
      'Team sharing',
      'Cloud backup',
      'Sermon scheduler',
      'Study questions generator',
      'Service assets',
      'Sermon summarizer',
      'Personal researcher'
    ],
    priceId: 'price_1RZO4aGHho15WvCD7L8Xp1FR'
  },
  annual: {
    id: 'prod_SUMojgy2r1hQuZ',
    name: 'SermonBuild Pro',
    description: 'Full access to all SermonBuild features - annual billing (save 10%).',
    price: { annual: 324 },
    features: [
      'Unlimited sermons',
      'Unlimited sermon analyses',
      'Advanced formatting tools',
      'Series management',
      'Team sharing',
      'Cloud backup',
      'Sermon scheduler',
      'Study questions generator',
      'Service assets',
      'Sermon summarizer',
      'Personal researcher'
    ],
    priceId: 'price_1RZO4aGHho15WvCDQzu1Z7m5'
  }
};

export type ProductId = keyof typeof PRODUCTS;
export type BillingInterval = 'monthly' | 'annual' | 'lifetime';

// Series Access add-on — standalone, not included in Premium or any other tier
export const SERIES_ACCESS_PLAN = {
  monthly: { price: 7,  priceId: 'price_1TP1g1GHho15WvCD7RC2VF0c' },
  annual:  { price: 77, priceId: 'price_1TP1gmGHho15WvCDzIy2c5e4' }
};

export const SERIES_ACCESS_PRICE_IDS = new Set([
  SERIES_ACCESS_PLAN.monthly.priceId,
  SERIES_ACCESS_PLAN.annual.priceId
]);

// Collaborator plan — paid tier that keeps shared-sermon + AI access but no own library
export const COLLABORATOR_PLAN = {
  monthly: {
    price: 10,
    priceId: 'price_1TMXORGHho15WvCDWt0Wn0we'
  },
  annual: {
    price: 110,
    priceId: 'price_1TMXR8GHho15WvCDcDLy7onS'
  }
};

// Set of all price IDs that belong to the Collaborator plan (used by webhook to determine tier)
export const COLLABORATOR_PRICE_IDS = new Set([
  COLLABORATOR_PLAN.monthly.priceId,
  COLLABORATOR_PLAN.annual.priceId
]);
